import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import {
  addMonths,
  addQuarters,
  addWeeks,
  addYears,
  differenceInCalendarDays,
  eachDayOfInterval,
  eachMonthOfInterval,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  isWeekend,
  parseISO,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subMonths,
  subQuarters,
  subWeeks,
  subYears
} from 'date-fns';
import { motion } from 'framer-motion';
import { Chip } from '@mui/material';
import { Link } from 'react-router-dom';
import {
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  CircleSlash,
  Compass,
  CreditCard,
  Flame,
  Landmark,
  LineChart as LineChartIcon,
  PieChart as PieChartIcon,
  Search,
  Sparkles,
  Target,
  TrendingUp,
  Wallet
} from 'lucide-react';
import { Card } from '../ui/Card';
import { CategoryIcon } from '../ui/CategoryIcon';
import { supabase } from '../../services/supabaseClient';
import { useAvailableBalance } from '../../hooks/useAvailableBalance';

const CHART_COLORS = ['#0077ff', '#111111', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#ec4899'];
const AXIS_TICK = { fontSize: 11, fill: '#71717a', fontWeight: 700, fontFamily: 'Roboto, Inter, sans-serif' };

const amountFormat = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
const formatAmount = (value) => `Rs. ${amountFormat.format(Number(value || 0))}`;
const percent = (value) => `${Math.round(Number(value || 0))}%`;

const periodConfig = {
  week: {
    label: 'Week',
    start: (date) => startOfWeek(date, { weekStartsOn: 1 }),
    end: (date) => endOfWeek(date, { weekStartsOn: 1 }),
    add: addWeeks,
    sub: subWeeks,
    title: (date) => `${format(startOfWeek(date, { weekStartsOn: 1 }), 'dd MMM')} - ${format(endOfWeek(date, { weekStartsOn: 1 }), 'dd MMM yyyy')}`,
    previousLabel: 'Previous week'
  },
  month: {
    label: 'Month',
    start: startOfMonth,
    end: endOfMonth,
    add: addMonths,
    sub: subMonths,
    title: (date) => format(date, 'MMMM yyyy'),
    previousLabel: 'Previous month'
  },
  quarter: {
    label: 'Quarter',
    start: startOfQuarter,
    end: endOfQuarter,
    add: addQuarters,
    sub: subQuarters,
    title: (date) => `Q${Math.floor(date.getMonth() / 3) + 1} ${format(date, 'yyyy')}`,
    previousLabel: 'Previous quarter'
  },
  year: {
    label: 'Year',
    start: startOfYear,
    end: endOfYear,
    add: addYears,
    sub: subYears,
    title: (date) => format(date, 'yyyy'),
    previousLabel: 'Previous year'
  }
};

const toDateKey = (date) => format(date, 'yyyy-MM-dd');
const getRange = (mode, date) => ({ start: periodConfig[mode].start(date), end: periodConfig[mode].end(date) });
const getTransactionAmount = (transaction) => Number(transaction.debit || transaction.credit || 0);
const getCategoryName = (transaction) => transaction.categories?.name || 'Other';

const getChange = (current, previous) => previous > 0 ? ((current - previous) / previous) * 100 : current > 0 ? 100 : 0;
const getDeltaTone = (value, lowerIsBetter = false) => {
  const good = lowerIsBetter ? value <= 0 : value >= 0;
  return good ? 'text-emerald-700 bg-emerald-50' : 'text-rose-700 bg-rose-50';
};

const makeBuckets = (mode, start, end) => {
  if (mode === 'year' || mode === 'quarter') {
    return eachMonthOfInterval({ start, end }).map((month) => ({
      name: format(month, 'MMM'),
      dateLabel: format(month, 'MMMM yyyy'),
      startKey: toDateKey(startOfMonth(month)),
      endKey: toDateKey(endOfMonth(month)),
      expense: 0,
      income: 0,
      savings: 0
    }));
  }

  return eachDayOfInterval({ start, end }).map((day) => ({
    name: mode === 'week' ? format(day, 'EEE') : format(day, 'd'),
    dateLabel: format(day, 'dd MMM yyyy'),
    startKey: toDateKey(day),
    endKey: toDateKey(day),
    expense: 0,
    income: 0,
    savings: 0
  }));
};

const EmptyState = () => (
  <div className="flex min-h-[280px] flex-col items-center justify-center rounded-[28px] border border-dashed border-slate-200 bg-white px-6 text-center">
    <div className="grid h-16 w-16 place-items-center rounded-[24px] bg-slate-100 text-slate-500">
      <CircleSlash size={34} strokeWidth={1.6} />
    </div>
    <h3 className="mt-5 text-xl font-black text-slate-950">Start adding transactions to unlock financial insights.</h3>
    <p className="mt-2 max-w-md text-sm font-semibold leading-6 text-slate-500">
      Once transactions are available, this page will explain spending, savings, habits, budgets, and cash flow.
    </p>
  </div>
);

const LoadingSkeleton = () => (
  <div className="space-y-4">
    <div className="animate-pulse rounded-[32px] border border-slate-200 bg-white p-6">
      <div className="h-4 w-40 rounded-full bg-slate-100" />
      <div className="mt-5 h-12 w-56 rounded-full bg-slate-100" />
      <div className="mt-6 grid gap-3 md:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => <div key={index} className="h-24 rounded-[24px] bg-slate-100" />)}
      </div>
    </div>
    <div className="grid gap-4 md:grid-cols-3">
      {Array.from({ length: 3 }).map((_, index) => <div key={index} className="h-36 animate-pulse rounded-[28px] bg-slate-100" />)}
    </div>
  </div>
);

const MoneyTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-[18px] border border-slate-200 bg-white px-4 py-3 text-xs shadow-sm">
      <p className="font-black text-slate-700">{payload[0]?.payload?.dateLabel || label}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} className="mt-1 font-bold" style={{ color: entry.color }}>
          {entry.name || entry.dataKey}: {formatAmount(entry.value)}
        </p>
      ))}
    </div>
  );
};

const SectionHeader = ({ eyebrow, title, icon: Icon }) => (
  <div className="flex items-center justify-between gap-3">
    <div className="min-w-0">
      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">{eyebrow}</p>
      <h2 className="mt-1 text-xl font-black tracking-tight text-slate-950 md:text-2xl">{title}</h2>
    </div>
    {Icon && (
      <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-slate-100 text-slate-700">
        <Icon size={19} />
      </div>
    )}
  </div>
);

export default function StatisticsPage({ user }) {
  const [periodMode, setPeriodMode] = useState('month');
  const [anchorDate, setAnchorDate] = useState(new Date());
  const [currentTransactions, setCurrentTransactions] = useState([]);
  const [previousTransactions, setPreviousTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllCategories, setShowAllCategories] = useState(false);
  const availableBalance = useAvailableBalance(user?.id);

  const activeConfig = periodConfig[periodMode];
  const currentRange = useMemo(() => getRange(periodMode, anchorDate), [anchorDate, periodMode]);
  const previousAnchor = useMemo(() => activeConfig.sub(anchorDate, 1), [activeConfig, anchorDate]);
  const previousRange = useMemo(() => getRange(periodMode, previousAnchor), [periodMode, previousAnchor]);

  const fetchPeriodTransactions = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const { data, error } = await supabase
      .from('transactions')
      .select('*, categories(name, type, icon_slug), subcategories(name)')
      .eq('user_id', user.id)
      .gte('date', format(previousRange.start, 'yyyy-MM-dd'))
      .lte('date', format(currentRange.end, 'yyyy-MM-dd'))
      .order('date', { ascending: true });

    if (!error) {
      const records = data || [];
      const currentStart = format(currentRange.start, 'yyyy-MM-dd');
      const currentEnd = format(currentRange.end, 'yyyy-MM-dd');
      const previousStart = format(previousRange.start, 'yyyy-MM-dd');
      const previousEnd = format(previousRange.end, 'yyyy-MM-dd');
      setCurrentTransactions(records.filter((t) => t.date >= currentStart && t.date <= currentEnd));
      setPreviousTransactions(records.filter((t) => t.date >= previousStart && t.date <= previousEnd));
    }

    setLoading(false);
  }, [currentRange, previousRange, user?.id]);

  useEffect(() => {
    fetchPeriodTransactions();
  }, [fetchPeriodTransactions]);

  const spendingData = useMemo(() => {
    const buckets = makeBuckets(periodMode, currentRange.start, currentRange.end);
    return buckets.map((bucket) => {
      const transactions = currentTransactions.filter((t) => t.date >= bucket.startKey && t.date <= bucket.endKey);
      const expense = transactions.reduce((sum, t) => sum + Number(t.debit || 0), 0);
      const income = transactions.reduce((sum, t) => sum + Number(t.credit || 0), 0);
      return { ...bucket, expense, income, savings: income - expense, cashFlow: income - expense };
    });
  }, [currentRange, currentTransactions, periodMode]);

  const previousSpendingData = useMemo(() => {
    const buckets = makeBuckets(periodMode, previousRange.start, previousRange.end);
    return buckets.map((bucket) => {
      const transactions = previousTransactions.filter((t) => t.date >= bucket.startKey && t.date <= bucket.endKey);
      const expense = transactions.reduce((sum, t) => sum + Number(t.debit || 0), 0);
      const income = transactions.reduce((sum, t) => sum + Number(t.credit || 0), 0);
      return { ...bucket, expense, income, savings: income - expense };
    });
  }, [periodMode, previousRange, previousTransactions]);

  const totals = useMemo(() => {
    const currentExpense = currentTransactions.reduce((sum, t) => sum + Number(t.debit || 0), 0);
    const currentIncome = currentTransactions.reduce((sum, t) => sum + Number(t.credit || 0), 0);
    const previousExpense = previousTransactions.reduce((sum, t) => sum + Number(t.debit || 0), 0);
    const previousIncome = previousTransactions.reduce((sum, t) => sum + Number(t.credit || 0), 0);
    const dayCount = Math.max(1, differenceInCalendarDays(currentRange.end, currentRange.start) + 1);
    const expenseRows = currentTransactions.filter((t) => Number(t.debit || 0) > 0);
    const largestExpense = [...expenseRows].sort((a, b) => Number(b.debit || 0) - Number(a.debit || 0))[0] || null;
    const dayTotals = spendingData.map((day) => ({ ...day })).filter((day) => day.expense > 0);
    const highestDay = [...dayTotals].sort((a, b) => b.expense - a.expense)[0] || null;
    const lowestDay = [...dayTotals].sort((a, b) => a.expense - b.expense)[0] || null;

    return {
      currentExpense,
      currentIncome,
      previousExpense,
      previousIncome,
      net: currentIncome - currentExpense,
      previousNet: previousIncome - previousExpense,
      savingsRate: currentIncome > 0 ? ((currentIncome - currentExpense) / currentIncome) * 100 : 0,
      previousSavingsRate: previousIncome > 0 ? ((previousIncome - previousExpense) / previousIncome) * 100 : 0,
      expenseChange: getChange(currentExpense, previousExpense),
      incomeChange: getChange(currentIncome, previousIncome),
      averageDailySpend: currentExpense / dayCount,
      previousAverageDailySpend: previousExpense / Math.max(1, differenceInCalendarDays(previousRange.end, previousRange.start) + 1),
      largestExpense,
      highestDay,
      lowestDay,
      transactionCount: currentTransactions.length,
      previousTransactionCount: previousTransactions.length,
      budgetUsage: currentIncome > 0 ? Math.min(100, (currentExpense / currentIncome) * 100) : 0,
      previousBudgetUsage: previousIncome > 0 ? Math.min(100, (previousExpense / previousIncome) * 100) : 0,
      monthlyBurnRate: (currentExpense / dayCount) * 30
    };
  }, [currentRange, currentTransactions, previousRange, previousTransactions, spendingData]);

  const categoryData = useMemo(() => {
    const current = currentTransactions.reduce((acc, t) => {
      if (Number(t.debit || 0) <= 0) return acc;
      const name = getCategoryName(t);
      if (!acc[name]) acc[name] = { name, value: 0, iconSlug: t.categories?.icon_slug, subcategories: new Set() };
      acc[name].value += Number(t.debit || 0);
      if (t.subcategories?.name) acc[name].subcategories.add(t.subcategories.name);
      return acc;
    }, {});

    const previous = previousTransactions.reduce((acc, t) => {
      if (Number(t.debit || 0) <= 0) return acc;
      const name = getCategoryName(t);
      acc[name] = (acc[name] || 0) + Number(t.debit || 0);
      return acc;
    }, {});

    return Object.values(current)
      .map((item) => ({
        ...item,
        subcategories: Array.from(item.subcategories),
        percent: totals.currentExpense > 0 ? (item.value / totals.currentExpense) * 100 : 0,
        previousValue: previous[item.name] || 0,
        change: getChange(item.value, previous[item.name] || 0)
      }))
      .sort((a, b) => b.value - a.value);
  }, [currentTransactions, previousTransactions, totals.currentExpense]);

  const visibleCategories = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const source = showAllCategories ? categoryData : categoryData.slice(0, 5);
    if (!query) return source;
    return categoryData.filter((item) => [item.name, ...item.subcategories].join(' ').toLowerCase().includes(query));
  }, [categoryData, searchQuery, showAllCategories]);

  const financialScore = useMemo(() => {
    const savings = totals.savingsRate >= 25 ? 20 : totals.savingsRate >= 15 ? 12 : totals.savingsRate >= 5 ? 5 : -8;
    const flow = totals.net >= 0 ? 18 : -15;
    const spending = totals.expenseChange <= -10 ? 16 : totals.expenseChange <= 0 ? 8 : totals.expenseChange > 20 ? -14 : -6;
    const concentration = categoryData[0]?.percent > 45 ? -8 : categoryData[0]?.percent > 30 ? -3 : 4;
    return Math.max(0, Math.min(100, Math.round(58 + savings + flow + spending + concentration)));
  }, [categoryData, totals]);

  const insightCards = useMemo(() => {
    const top = categoryData[0];
    const cards = [
      {
        title: totals.expenseChange <= 0 ? `You spent ${percent(Math.abs(totals.expenseChange))} less than last period.` : `You spent ${percent(totals.expenseChange)} more than last period.`,
        detail: `${formatAmount(totals.currentExpense)} went out this period versus ${formatAmount(totals.previousExpense)} previously.`,
        action: totals.expenseChange <= 0 ? 'Protect the improvement by keeping flexible spending capped.' : 'Review the categories below and set a smaller weekly limit.',
        icon: totals.expenseChange <= 0 ? ArrowDownRight : ArrowUpRight,
        tone: totals.expenseChange <= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
      },
      {
        title: totals.net >= 0 ? `You have ${formatAmount(totals.net)} positive cash flow.` : `You are short by ${formatAmount(Math.abs(totals.net))}.`,
        detail: `Savings rate is ${percent(totals.savingsRate)} after income and expenses are matched.`,
        action: totals.net >= 0 ? 'Move part of the surplus into savings before it gets spent.' : 'Pause optional expenses until income catches up.',
        icon: Wallet,
        tone: totals.net >= 0 ? 'bg-sky-50 text-sky-700' : 'bg-amber-50 text-amber-700'
      },
      {
        title: top ? `${top.name} is taking ${percent(top.percent)} of spending.` : 'No dominant spending category yet.',
        detail: top ? `${formatAmount(top.value)} is concentrated in this category.` : 'Add more expenses to reveal the pressure points.',
        action: top ? `Audit recent ${top.name} transactions and reduce the next allocation.` : 'Keep adding transactions consistently.',
        icon: Target,
        tone: 'bg-violet-50 text-violet-700'
      },
      {
        title: totals.incomeChange >= 0 ? `Income increased by ${percent(totals.incomeChange)}.` : `Income decreased by ${percent(Math.abs(totals.incomeChange))}.`,
        detail: `Current income is ${formatAmount(totals.currentIncome)}.`,
        action: totals.incomeChange >= 0 ? 'Assign new income to savings or priority categories.' : 'Keep spending conservative until income normalizes.',
        icon: Landmark,
        tone: 'bg-blue-50 text-blue-700'
      }
    ];
    return cards;
  }, [categoryData, totals]);

  const habitInsights = useMemo(() => {
    const expenseRows = currentTransactions.filter((t) => Number(t.debit || 0) > 0);
    const weekdayTotals = expenseRows.reduce((acc, t) => {
      const day = format(parseISO(t.date), 'EEEE');
      acc[day] = (acc[day] || 0) + Number(t.debit || 0);
      return acc;
    }, {});
    const topDay = Object.entries(weekdayTotals).sort(([, a], [, b]) => b - a)[0];
    const weekendSpend = expenseRows.filter((t) => isWeekend(parseISO(t.date))).reduce((sum, t) => sum + Number(t.debit || 0), 0);
    const afterIncomeDates = currentTransactions.filter((t) => Number(t.credit || 0) > 0).map((t) => t.date);
    const afterSalarySpend = expenseRows.filter((t) => afterIncomeDates.some((date) => differenceInCalendarDays(parseISO(t.date), parseISO(date)) >= 0 && differenceInCalendarDays(parseISO(t.date), parseISO(date)) <= 3)).reduce((sum, t) => sum + Number(t.debit || 0), 0);
    const topCategory = categoryData[0];

    return [
      topDay && { title: `You spend most on ${topDay[0]}s.`, detail: `${formatAmount(topDay[1])} usually leaves your account on this day.`, action: 'Plan that day with a smaller discretionary cap.' },
      weekendSpend > 0 && { title: 'Weekend spending is a visible habit.', detail: `${formatAmount(weekendSpend)} was spent on weekends this period.`, action: 'Set a weekend cash limit before Friday.' },
      afterSalarySpend > 0 && { title: 'Spending rises soon after income arrives.', detail: `${formatAmount(afterSalarySpend)} was spent within three days after income entries.`, action: 'Allocate savings immediately after salary.' },
      topCategory && { title: `${topCategory.name} is your repeated pressure point.`, detail: `It currently leads all categories at ${formatAmount(topCategory.value)}.`, action: 'Create a weekly review for this category.' },
      totals.savingsRate > totals.previousSavingsRate && { title: 'Your financial health is improving.', detail: `Savings rate rose from ${percent(totals.previousSavingsRate)} to ${percent(totals.savingsRate)}.`, action: 'Keep the same allocation rhythm next period.' }
    ].filter(Boolean).slice(0, 5);
  }, [categoryData, currentTransactions, totals]);

  const comparisonCards = useMemo(() => [
    { label: 'Income', current: totals.currentIncome, previous: totals.previousIncome, lowerIsBetter: false },
    { label: 'Expenses', current: totals.currentExpense, previous: totals.previousExpense, lowerIsBetter: true },
    { label: 'Savings', current: totals.net, previous: totals.previousNet, lowerIsBetter: false },
    { label: 'Budget usage', current: totals.budgetUsage, previous: totals.previousBudgetUsage, suffix: '%', lowerIsBetter: true },
    { label: 'Average daily spend', current: totals.averageDailySpend, previous: totals.previousAverageDailySpend, lowerIsBetter: true },
    { label: 'Transaction count', current: totals.transactionCount, previous: totals.previousTransactionCount, plain: true, lowerIsBetter: true },
    { label: 'Largest expense', current: Number(totals.largestExpense?.debit || 0), previous: 0, lowerIsBetter: true }
  ], [totals]);

  const matchingTransactions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return [];
    return currentTransactions.filter((transaction) => [
      getCategoryName(transaction),
      transaction.subcategories?.name || '',
      transaction.description || '',
      format(parseISO(transaction.date), 'MMMM yyyy')
    ].join(' ').toLowerCase().includes(query)).slice(0, 6);
  }, [currentTransactions, searchQuery]);

  const filteredChartData = useMemo(() => {
    if (activeFilter === 'income') return spendingData.map((d) => ({ ...d, expense: 0 }));
    if (activeFilter === 'expense') return spendingData.map((d) => ({ ...d, income: 0 }));
    if (activeFilter === 'savings') return spendingData.map((d) => ({ ...d, expense: 0, income: 0 }));
    return spendingData;
  }, [activeFilter, spendingData]);

  const hasData = currentTransactions.length > 0;

  if (loading) {
    return <div className="mx-auto max-w-7xl pb-24"><LoadingSkeleton /></div>;
  }

  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="mx-auto max-w-7xl space-y-5 pb-24 text-slate-950">
      <header className="space-y-4">
        <div>
          <p className="text-[11px] font-black uppercase tracking-[0.24em] text-slate-500">Statistics and insights</p>
          <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-950 md:text-4xl">Financial insights center</h1>
          <p className="mt-2 max-w-2xl text-sm font-semibold leading-6 text-slate-500">Clear answers first, charts second, every recommendation grounded in your existing transaction data.</p>
        </div>

        <div className="sticky top-3 z-20 rounded-[28px] border border-slate-200 bg-white/95 p-3 backdrop-blur">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex gap-2 overflow-x-auto pb-1">
              {['week', 'month', 'quarter', 'year'].map((mode) => (
                <Chip key={mode} label={periodConfig[mode].label} clickable color={periodMode === mode ? 'primary' : 'default'} variant={periodMode === mode ? 'filled' : 'outlined'} onClick={() => setPeriodMode(mode)} />
              ))}
              {['all', 'income', 'expense', 'savings', 'budget', 'tracker', 'categories'].map((filter) => (
                <Chip key={filter} label={filter === 'all' ? 'All' : filter === 'tracker' ? 'Money Tracker' : filter[0].toUpperCase() + filter.slice(1)} clickable color={activeFilter === filter ? 'primary' : 'default'} variant={activeFilter === filter ? 'filled' : 'outlined'} onClick={() => setActiveFilter(filter)} />
              ))}
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <label className="flex min-h-[44px] items-center gap-2 rounded-full border border-slate-200 bg-white px-4 text-sm text-slate-500">
                <Search size={16} />
                <input value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} className="w-full min-w-0 bg-transparent text-sm font-semibold outline-none sm:w-64" placeholder="Search categories, months, transactions" />
              </label>
              <div className="flex items-center justify-between gap-2 rounded-full border border-slate-200 px-2 py-1">
                <button className="m3-icon-button h-10 w-10" onClick={() => setAnchorDate(activeConfig.sub(anchorDate, 1))} aria-label="Previous period"><ChevronLeft size={18} /></button>
                <span className="min-w-[130px] text-center text-sm font-black">{activeConfig.title(anchorDate)}</span>
                <button className="m3-icon-button h-10 w-10" onClick={() => setAnchorDate(activeConfig.add(anchorDate, 1))} aria-label="Next period"><ChevronRight size={18} /></button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <Card className="rounded-[28px] bg-slate-950 p-5 text-white md:p-6">
        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-300">Current available balance</p>
        <p className="mt-2 text-3xl font-black tracking-tight">
          {availableBalance.loading ? '—' : formatAmount(availableBalance.balance)}
        </p>
        <p className="mt-1 text-sm font-semibold text-slate-300">
          Includes carry-forward and all transactions through today.
        </p>
        {availableBalance.error && (
          <p className="mt-3 text-xs font-semibold text-rose-200" role="alert">
            Could not load available balance: {availableBalance.error}
          </p>
        )}
      </Card>

      {!hasData ? <EmptyState /> : (
        <>
          <Card className="rounded-[32px] bg-slate-950 p-5 text-white md:p-7">
            <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr] lg:items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-[0.16em] text-white">
                  <Sparkles size={14} /> Financial health hero
                </div>
                <h2 className="mt-5 text-3xl font-black tracking-tight md:text-5xl">{financialScore}/100 monthly financial score</h2>
                <p className="mt-4 max-w-2xl text-sm font-semibold leading-6 text-slate-300">
                  {totals.expenseChange <= 0 ? 'You are spending less than the previous period.' : 'Spending is running hotter than the previous period.'} {totals.net >= 0 ? 'Cash flow is positive.' : 'Cash flow needs attention.'}
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  ['Period income', formatAmount(totals.currentIncome), 'text-emerald-300'],
                  ['Total expenses', formatAmount(totals.currentExpense), 'text-rose-300'],
                  ['Savings rate', percent(totals.savingsRate), 'text-sky-300'],
                  ['Net cash flow', formatAmount(totals.net), totals.net >= 0 ? 'text-emerald-300' : 'text-rose-300'],
                  ['Vs previous', `${totals.expenseChange >= 0 ? '+' : ''}${percent(totals.expenseChange)}`, totals.expenseChange <= 0 ? 'text-emerald-300' : 'text-amber-300']
                ].map(([label, value, tone]) => (
                  <div key={label} className="rounded-[24px] border border-white/10 bg-white/10 p-4">
                    <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-300">{label}</p>
                    <p className={`mt-3 text-xl font-black ${tone}`}>{value}</p>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-4" aria-label="AI financial summary">
            {insightCards.map((insight, index) => {
              const Icon = insight.icon;
              return (
                <motion.div key={insight.title} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.04 }} className="rounded-[28px] border border-slate-200 bg-white p-5">
                  <div className={`inline-grid h-11 w-11 place-items-center rounded-full ${insight.tone}`}><Icon size={19} /></div>
                  <h3 className="mt-4 text-lg font-black leading-6 text-slate-950">{insight.title}</h3>
                  <p className="mt-2 text-sm font-semibold leading-6 text-slate-500">{insight.detail}</p>
                  <p className="mt-4 flex items-start gap-2 text-sm font-black leading-5 text-slate-800"><Compass size={16} className="mt-0.5 shrink-0" />{insight.action}</p>
                </motion.div>
              );
            })}
          </section>

          <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" aria-label="Spending overview">
            {[
              ['Total expenses', formatAmount(totals.currentExpense), CreditCard],
              ['Average daily spend', formatAmount(totals.averageDailySpend), CalendarDays],
              ['Highest spending day', totals.highestDay ? `${totals.highestDay.name} (${formatAmount(totals.highestDay.expense)})` : 'No expenses', TrendingUp],
              ['Lowest spending day', totals.lowestDay ? `${totals.lowestDay.name} (${formatAmount(totals.lowestDay.expense)})` : 'No expenses', ArrowDownRight],
              ['Largest transaction', totals.largestExpense ? formatAmount(totals.largestExpense.debit) : 'No expenses', Wallet],
              ['Transactions', String(totals.transactionCount), BarChart3],
              ['Monthly burn rate', formatAmount(totals.monthlyBurnRate), Flame]
            ].map(([label, value, Icon]) => (
              <Card key={label} className="rounded-[28px] border border-slate-200 p-5">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">{label}</p>
                  <div className="grid h-10 w-10 place-items-center rounded-full bg-slate-100 text-slate-700"><Icon size={17} /></div>
                </div>
                <p className="mt-4 text-xl font-black text-slate-950">{value}</p>
              </Card>
            ))}
          </section>

          <section className="rounded-[32px] border border-slate-200 bg-white p-5 md:p-6" aria-label="Category analysis">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <SectionHeader eyebrow="Category analysis" title="Top categories with plain-language context" icon={PieChartIcon} />
              <button onClick={() => setShowAllCategories((value) => !value)} className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full border border-slate-200 px-4 text-sm font-black text-slate-700">
                {showAllCategories ? 'Show top 5' : 'View all categories'} <ArrowRight size={16} />
              </button>
            </div>
            <div className="mt-6 grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
              {visibleCategories.map((item) => (
                <Link key={item.name} to={`/transactions?category=${encodeURIComponent(item.name)}`} className="rounded-[26px] border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-slate-300">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="grid h-12 w-12 shrink-0 place-items-center rounded-[18px] bg-slate-950 text-white">
                        <CategoryIcon iconSlug={item.iconSlug} className="h-5 w-5" size={15} />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-base font-black text-slate-950">{item.name}</p>
                        <p className="text-sm font-semibold text-slate-500">{percent(item.percent)} of total spend</p>
                      </div>
                    </div>
                    <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-black ${getDeltaTone(item.change, true)}`}>
                      {item.change >= 0 ? '+' : ''}{percent(item.change)}
                    </span>
                  </div>
                  <p className="mt-4 text-2xl font-black text-slate-950">{formatAmount(item.value)}</p>
                  <div className="mt-4 h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-slate-950" style={{ width: `${Math.min(100, item.percent)}%` }} /></div>
                  <p className="mt-3 text-sm font-bold text-slate-500">{item.change >= 0 ? 'Above last period. Consider a tighter limit.' : 'Below last period. Keep this trend.'}</p>
                </Link>
              ))}
            </div>
          </section>

          <section className="rounded-[32px] border border-slate-200 bg-white p-5 md:p-6" aria-label="Interactive charts">
            <SectionHeader eyebrow="Interactive charts" title="Trends, distribution, cash flow, savings, and budget usage" icon={LineChartIcon} />
            <div className="mt-6 grid gap-4 xl:grid-cols-2">
              <div className="rounded-[26px] border border-slate-200 p-4">
                <p className="text-sm font-black text-slate-700">Monthly spending trend</p>
                <div className="mt-4 h-64"><ResponsiveContainer><BarChart data={filteredChartData}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} /><YAxis tick={AXIS_TICK} tickFormatter={(v) => `Rs. ${amountFormat.format(v)}`} width={72} axisLine={false} tickLine={false} /><Tooltip content={<MoneyTooltip />} /><Bar dataKey="expense" name="Expense" fill="#0077ff" radius={[9, 9, 0, 0]} /></BarChart></ResponsiveContainer></div>
              </div>
              <div className="rounded-[26px] border border-slate-200 p-4">
                <p className="text-sm font-black text-slate-700">Income vs expense</p>
                <div className="mt-4 h-64"><ResponsiveContainer><BarChart data={filteredChartData}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} /><YAxis tick={AXIS_TICK} tickFormatter={(v) => `Rs. ${amountFormat.format(v)}`} width={72} axisLine={false} tickLine={false} /><Tooltip content={<MoneyTooltip />} /><Legend /><Bar dataKey="income" name="Income" fill="#10b981" radius={[8, 8, 0, 0]} /><Bar dataKey="expense" name="Expense" fill="#ef4444" radius={[8, 8, 0, 0]} /></BarChart></ResponsiveContainer></div>
              </div>
              <div className="rounded-[26px] border border-slate-200 p-4">
                <p className="text-sm font-black text-slate-700">Category distribution</p>
                <div className="mt-4 h-64"><ResponsiveContainer><PieChart><Pie data={categoryData} dataKey="value" nameKey="name" innerRadius={58} outerRadius={92} paddingAngle={3}>{categoryData.map((entry, index) => <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />)}</Pie><Tooltip content={<MoneyTooltip />} /><Legend /></PieChart></ResponsiveContainer></div>
              </div>
              <div className="rounded-[26px] border border-slate-200 p-4">
                <p className="text-sm font-black text-slate-700">Cash flow</p>
                <div className="mt-4 h-64"><ResponsiveContainer><AreaChart data={filteredChartData}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} /><YAxis tick={AXIS_TICK} tickFormatter={(v) => `Rs. ${amountFormat.format(v)}`} width={72} axisLine={false} tickLine={false} /><Tooltip content={<MoneyTooltip />} /><Area type="monotone" dataKey="cashFlow" name="Cash flow" stroke="#111111" fill="#eaf4ff" strokeWidth={3} /></AreaChart></ResponsiveContainer></div>
              </div>
              <div className="rounded-[26px] border border-slate-200 p-4">
                <p className="text-sm font-black text-slate-700">Savings trend</p>
                <div className="mt-4 h-64"><ResponsiveContainer><LineChart data={filteredChartData}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} /><YAxis tick={AXIS_TICK} tickFormatter={(v) => `Rs. ${amountFormat.format(v)}`} width={72} axisLine={false} tickLine={false} /><Tooltip content={<MoneyTooltip />} /><Line type="monotone" dataKey="savings" name="Savings" stroke="#10b981" strokeWidth={3} dot={{ r: 4 }} /></LineChart></ResponsiveContainer></div>
              </div>
              <div className="rounded-[26px] border border-slate-200 p-4">
                <p className="text-sm font-black text-slate-700">Budget usage and money tracker trend</p>
                <div className="mt-4 h-64"><ResponsiveContainer><LineChart data={[...previousSpendingData.map((d) => ({ ...d, period: activeConfig.previousLabel })), ...spendingData.map((d) => ({ ...d, period: 'Current' }))]}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} /><YAxis tick={AXIS_TICK} tickFormatter={(v) => `Rs. ${amountFormat.format(v)}`} width={72} axisLine={false} tickLine={false} /><Tooltip content={<MoneyTooltip />} /><Legend /><Line type="monotone" dataKey="expense" name="Budget usage" stroke="#f59e0b" strokeWidth={3} dot={false} /><Line type="monotone" dataKey="income" name="Money tracker trend" stroke="#8b5cf6" strokeWidth={3} dot={false} /></LineChart></ResponsiveContainer></div>
              </div>
            </div>
          </section>

          <section className="grid gap-4 xl:grid-cols-[1fr_0.9fr]">
            <Card className="rounded-[32px] border border-slate-200 p-5 md:p-6">
              <SectionHeader eyebrow="Month comparison" title="Current vs previous period" icon={BarChart3} />
              <div className="mt-6 grid gap-3 md:grid-cols-2">
                {comparisonCards.map((card) => {
                  const delta = card.current - card.previous;
                  const deltaPct = getChange(card.current, card.previous);
                  return (
                    <div key={card.label} className="rounded-[24px] border border-slate-200 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <p className="text-sm font-black text-slate-700">{card.label}</p>
                        <span className={`rounded-full px-2.5 py-1 text-xs font-black ${getDeltaTone(delta, card.lowerIsBetter)}`}>{delta >= 0 ? '+' : ''}{card.plain ? amountFormat.format(delta) : card.suffix ? percent(delta) : formatAmount(delta)}</span>
                      </div>
                      <p className="mt-3 text-xl font-black text-slate-950">{card.plain ? amountFormat.format(card.current) : card.suffix ? percent(card.current) : formatAmount(card.current)}</p>
                      <p className="mt-1 text-xs font-bold text-slate-500">Previous: {card.plain ? amountFormat.format(card.previous) : card.suffix ? percent(card.previous) : formatAmount(card.previous)} ({deltaPct >= 0 ? '+' : ''}{percent(deltaPct)})</p>
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card className="rounded-[32px] border border-slate-200 p-5 md:p-6">
              <SectionHeader eyebrow="Financial habits" title="Patterns your data keeps repeating" icon={Sparkles} />
              <div className="mt-6 space-y-3">
                {habitInsights.map((habit) => (
                  <div key={habit.title} className="rounded-[24px] border border-slate-200 p-4">
                    <p className="text-base font-black text-slate-950">{habit.title}</p>
                    <p className="mt-1 text-sm font-semibold leading-6 text-slate-500">{habit.detail}</p>
                    <p className="mt-3 text-sm font-black text-slate-800">Suggested action: {habit.action}</p>
                  </div>
                ))}
              </div>
            </Card>
          </section>

          <section className="grid gap-4 xl:grid-cols-3">
            <Card className="rounded-[32px] border border-slate-200 p-5 md:p-6">
              <SectionHeader eyebrow="Budget intelligence" title="Allocation pressure" icon={Target} />
              <div className="mt-6 space-y-3">
                {[
                  ['Most overspent category', categoryData[0]?.name || 'No category yet'],
                  ['Best performing category', categoryData[categoryData.length - 1]?.name || 'No category yet'],
                  ['Budget completion', `${percent(totals.budgetUsage)} of income used`],
                  ['Categories near limit', categoryData.filter((item) => item.percent >= 20).map((item) => item.name).join(', ') || 'None yet'],
                  ['Money left to allocate', formatAmount(Math.max(0, totals.currentIncome - totals.currentExpense))]
                ].map(([label, value]) => (
                  <div key={label} className="rounded-[22px] border border-slate-200 p-4">
                    <p className="text-xs font-black uppercase tracking-[0.14em] text-slate-500">{label}</p>
                    <p className="mt-2 text-base font-black text-slate-950">{value}</p>
                  </div>
                ))}
                <Link to="/budget-allocation" className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full bg-slate-950 px-4 text-sm font-black text-white">Open budget workspace <ArrowRight size={16} /></Link>
              </div>
            </Card>

            <Card className="rounded-[32px] border border-slate-200 p-5 md:p-6">
              <SectionHeader eyebrow="Money tracker insights" title="Outstanding commitments" icon={Wallet} />
              <div className="mt-6 grid gap-3">
                {[
                  ['Outstanding lent', 'Open tracker to review'],
                  ['Outstanding borrowed', 'Open tracker to review'],
                  ['Settlement rate', 'Ready for tracker data'],
                  ['Average collection time', 'Ready for tracker data'],
                  ['Most frequent contact', 'Ready for tracker data'],
                  ['Savings growth', percent(Math.max(0, totals.savingsRate))]
                ].map(([label, value]) => (
                  <div key={label} className="rounded-[22px] border border-slate-200 p-4">
                    <p className="text-xs font-black uppercase tracking-[0.14em] text-slate-500">{label}</p>
                    <p className="mt-2 text-base font-black text-slate-950">{value}</p>
                  </div>
                ))}
                <Link to="/money-tracker" className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full border border-slate-200 px-4 text-sm font-black text-slate-700">Open money tracker <ArrowRight size={16} /></Link>
              </div>
            </Card>

            <Card className="rounded-[32px] border border-slate-200 p-5 md:p-6">
              <SectionHeader eyebrow="Recommendations" title="Your next best moves" icon={Compass} />
              <div className="mt-6 space-y-3">
                {[
                  `Reduce ${categoryData[0]?.name || 'your top category'} by ${formatAmount(Math.max(1000, totals.currentExpense * 0.08))} to reach a healthier savings rate.`,
                  `${categoryData[0]?.name || 'Top spending'} changed by ${percent(categoryData[0]?.change || 0)}. Review the latest transactions before the pattern repeats.`,
                  totals.net > 0 ? `Move ${formatAmount(Math.max(500, totals.net * 0.15))} from surplus into savings.` : `Recover ${formatAmount(Math.abs(totals.net))} by delaying flexible purchases.`,
                  'Allocate any remaining budget before adding new discretionary spend.'
                ].map((recommendation) => (
                  <div key={recommendation} className="rounded-[24px] border border-slate-200 p-4">
                    <p className="text-base font-black leading-6 text-slate-950">{recommendation}</p>
                    <p className="mt-2 text-sm font-semibold text-slate-500">Suggested action: open the relevant workspace and adjust the next allocation.</p>
                  </div>
                ))}
              </div>
            </Card>
          </section>

          {searchQuery && matchingTransactions.length > 0 && (
            <Card className="rounded-[32px] border border-slate-200 p-5 md:p-6">
              <SectionHeader eyebrow="Search results" title="Matches from transactions" icon={Search} />
              <div className="mt-6 grid gap-3 md:grid-cols-2">
                {matchingTransactions.map((transaction) => (
                  <Link key={transaction.id} to={`/transactions?search=${encodeURIComponent(searchQuery)}`} className="flex items-center justify-between gap-3 rounded-[24px] border border-slate-200 p-4">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-black text-slate-950">{getCategoryName(transaction)}</p>
                      <p className="truncate text-sm font-semibold text-slate-500">{transaction.description || format(parseISO(transaction.date), 'dd MMM yyyy')}</p>
                    </div>
                    <p className="shrink-0 text-sm font-black text-slate-950">{formatAmount(getTransactionAmount(transaction))}</p>
                  </Link>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </motion.div>
  );
}
